import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import icArrowLeft from "@/assets/icons/ic-arrow-narrow-left.svg";
import {
  UserManagementService,
  UserPrjRes,
  IProjectStatus,
} from "@/services/user-management-service";
import { formatDate, STATUS_CONFIG_ADMIN } from "@/lib/utils";

const PAGE_SIZE = 10;

const ManageUserProjectListPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [projects, setProjects] = useState<UserPrjRes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [userName, setUserName] = useState("");

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<UserPrjRes | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const [userResponse, projectResponse] = await Promise.all([
          UserManagementService.getById(id),
          UserManagementService.getListUserProject({
            user_Id: id,
            page: currentPage,
            page_size: PAGE_SIZE,
          }),
        ]);

        const userData = userResponse.data.data;
        const projectsData = projectResponse.data.data?.items ?? [];
        const meta = projectResponse.data.data;

        if (userData) {
          setUserName(userData.requester_name);
        }

        setProjects(projectsData);
        setTotal(meta?.total ?? 0);
        setTotalPages(Math.ceil((meta?.total ?? 0) / PAGE_SIZE) || 1);
      } catch (err) {
        setError("Failed to load projects");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, currentPage]);

  const handleBack = () => {
    navigate(`/manage-user/${id}`);
  };

  const handleDeleteClick = (project: UserPrjRes) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete || !id) return;

    try {
      setDeleteLoading(true);
      await UserManagementService.deleteUserProject(id, projectToDelete.id);
      toast({
        title: "프로젝트가 삭제되었습니다.",
      });
      // Refresh the list
      const projectResponse = await UserManagementService.getListUserProject({
        user_Id: id,
        page: currentPage,
        page_size: PAGE_SIZE,
      });
      const items = projectResponse.data.data?.items ?? [];
      const meta = projectResponse.data.data;

      if (items.length === 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        setProjects(items);
        setTotal(meta?.total ?? 0);
        setTotalPages(Math.ceil((meta?.total ?? 0) / PAGE_SIZE) || 1);
      }
    } catch (err) {
      toast({
        title: "삭제 실패",
        description: "프로젝트 삭제 중 오류가 발생했습니다.",
        variant: "destructive",
      });
      console.error(err);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG_ADMIN[status] || STATUS_CONFIG_ADMIN.init;
    const statusComplete = status === "completed";
    return (
      <div
        className={`flex items-center lg:text-[16px] text-[14px] justify-center w-fit gap-1 py-[2px] rounded ${config.bg} ${config.text} ${statusComplete ? "px-[21px]" : " px-[12.5px]"}`}
      >
        <p className="font-normal">{config.label}</p>
      </div>
    );
  };

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div className="min-h-screen" style={{ display: loading ? "none" : "block" }}>
        {/* Header */}
        <div className="flex items-center gap-1 mb-6">
          <Button
            onClick={handleBack}
            variant="ghost"
            size="icon"
            className="w-12 h-12  flex items-center justify-center"
          >
            <img src={icArrowLeft} alt="Back" className="" />
          </Button>
          <h1 className="text-[20px] font-bold text-[#000000]">{userName}님의 프로젝트</h1>
        </div>

        {/* Body Card */}
        <div className="bg-white rounded-[16px]">
          {/* Table */}
          <div className="border border-[rgba(0,0,0,0.09)] rounded-[4px] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#EBEBEB] border-b border-[rgba(0,0,0,0.09)]">
                  <TableHead className="text-[12px] font-medium text-[#000000] uppercase tracking-wide p-3">
                    프로젝트명
                  </TableHead>
                  <TableHead className="text-[12px] font-medium text-[#000000] uppercase tracking-wide p-3">
                    생성일
                  </TableHead>
                  <TableHead className="text-[12px] font-medium text-[#000000] uppercase tracking-wide p-3">
                    상태
                  </TableHead>
                  <TableHead className="text-[12px] font-medium text-[#000000] uppercase tracking-wide p-3">
                    보고서 열람
                  </TableHead>
                  <TableHead className="text-right text-[12px] font-medium text-[#000000] uppercase tracking-wide p-3">
                    관리
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center p-8 text-[#000000]">
                      프로젝트가 없습니다
                    </TableCell>
                  </TableRow>
                ) : (
                  projects.map((project) => (
                    <TableRow
                      key={project.id}
                      className="border-b border-[rgba(0,0,0,0.09)] last:border-b-0"
                    >
                      <TableCell className="p-3 w-[364px]">
                        <span className="text-[16px] text-[#000000]">{project.name}</span>
                      </TableCell>
                      <TableCell className="p-3 w-[218px]">
                        <span className="text-[16px] text-[#000000]">
                          {formatDate(project.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="p-3 w-[136px]">
                        {getStatusBadge(project.status)}
                      </TableCell>
                      <TableCell className="p-3 w-[136px]">
                        <button
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className="text-[#000000] font-bold underline text-[16px]"
                        >
                          보기
                        </button>
                      </TableCell>
                      <TableCell className="p-3 w-[191px]">
                        <div className="flex items-center justify-end gap-[8px]">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-[80px] h-[32px] text-[12px] font-bold text-[#000000] bg-[#F7DDCF]"
                            onClick={() => handleDeleteClick(project)}
                          >
                            삭제
                          </Button>
                          {project.status !== "completed" && project.status !== "deleted" ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-[80px] h-[32px] text-[12px] font-bold text-[#000000] border border-[rgba(0,0,0,0.15)]"
                              onClick={() => navigate(`/projects/${project.id}?mode=edit`)}
                            >
                              수정
                            </Button>
                          ) : (
                            <div className="w-[80px]" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          variant="danger"
          title="프로젝트 삭제 확인"
          description={`해당 프로젝트를 삭제하시겠습니까?`}
          confirmText="네"
          cancelText="아니요"
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
        />
      </div>
    </>
  );
};

export default ManageUserProjectListPage;
